import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { InputNumberMask } from '../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Radio } from '../../../../../../../CommonComponents/OPL/Radio/Radio';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { OptionType } from '../../../../../../../CommonComponents/OPL/Select/BaseSelect/types';
import { FormValues, PensionPlanType } from './PensionChange.meta';

interface Props {
  index: number;
  planTypeOptions: OptionType<number>[];
  ageOptions: OptionType<number>[];
  retirementAge: number;
}

export const PensionChangePanel = ({
  index,
  planTypeOptions,
  ageOptions,
  retirementAge,
}: Props) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FormValues>();

  const defaultCustomDistributionAge = ageOptions.some(o => o.value === retirementAge)
    ? retirementAge
    : ageOptions[0]?.value ?? retirementAge;

  const watchDistributionAtRetirement = watch(`Pensions.${index}.DistributionAtRetirement`);
  const watchIsInflationAdjusted = watch(`Pensions.${index}.IsInflationAdjusted`);
  const watchPlanType = watch(`Pensions.${index}.PlanType`);
  const isLumpSum = watchPlanType === PensionPlanType.LumpSum;
  const isJointSurvivor = watchPlanType === PensionPlanType.JointSurvivor;

  return (
    <Box
      display="flex"
      flexDirection="column"
      px="16"
      pb="32"
      css={`
        gap: ${({ theme }) => theme.space['16']}px;
      `}
    >
      <Controller
        name={`Pensions.${index}.PlanType`}
        control={control}
        render={({ field: { value, onChange } }) => (
          <Select
            label="Type"
            options={planTypeOptions}
            value={planTypeOptions.find(opt => opt.value === value)}
            onChange={option => {
              onChange(option?.value ?? PensionPlanType.SingleLife);
              if (option?.value === PensionPlanType.LumpSum) {
                setValue(`Pensions.${index}.IsInflationAdjusted`, false, { shouldValidate: true });
                setValue(`Pensions.${index}.InflationRate`, null, { shouldValidate: true });
              }
            }}
            isPortal
          />
        )}
      />

      {!isLumpSum && (
        <Controller
          control={control}
          name={`Pensions.${index}.MonthlyPayment`}
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Monthly Payment"
              decimalScale={2}
              allowNegative={false}
              leftIcon="far fa-dollar-sign"
              formVariant={errors.Pensions?.[index]?.MonthlyPayment ? 'error' : undefined}
              helperText={errors.Pensions?.[index]?.MonthlyPayment?.message}
              onValueChange={({ floatValue }) => onChange(floatValue ?? 0)}
              {...field}
            />
          )}
        />
      )}

      {isLumpSum && (
        <Controller
          control={control}
          name={`Pensions.${index}.LumpSumAmount`}
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Lump Sum Amount"
              decimalScale={2}
              allowNegative={false}
              leftIcon="far fa-dollar-sign"
              onValueChange={({ floatValue }) => onChange(floatValue ?? 0)}
              {...field}
            />
          )}
        />
      )}

      <Text fontSize="small" fontWeight={600} color="neutral.600" mt="4">
        Distribution Age
      </Text>

      <Controller
        name={`Pensions.${index}.DistributionAtRetirement`}
        control={control}
        render={({ field: { value, name, onChange } }) => (
          <Box
            display="flex"
            css={`
              gap: ${({ theme }) => theme.space['8']}px;
            `}
          >
            <Radio name={name} checked={value} onChange={() => onChange(true)}>
              <Text fontSize="small" fontWeight={600} color="neutral.600" mt="4">
                At Retirement
              </Text>
            </Radio>
            <Radio
              name={name}
              checked={!value}
              onChange={() => {
                onChange(false);
                setValue(`Pensions.${index}.DistributionAge`, defaultCustomDistributionAge, {
                  shouldValidate: true,
                });
              }}
            >
              <Text fontSize="small" fontWeight={600} color="neutral.600" mt="4">
                Custom Age
              </Text>
            </Radio>
          </Box>
        )}
      />

      {!watchDistributionAtRetirement && (
        <Controller
          name={`Pensions.${index}.DistributionAge`}
          control={control}
          render={({ field: { value, onChange, ref } }) => (
            <Select
              innerRef={ref}
              label="Distribution Age"
              value={ageOptions.find(option => option.value === value)}
              options={ageOptions}
              onChange={option => {
                onChange(option?.value ?? null);
              }}
              isPortal
            />
          )}
        />
      )}

      {!isLumpSum && (
        <>
          <Text fontSize="small" fontWeight={600} color="neutral.600" mt="4">
            Inflation Adjusted
          </Text>
          <Controller
            name={`Pensions.${index}.IsInflationAdjusted`}
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <Box
                display="flex"
                css={`
                  gap: ${({ theme }) => theme.space['8']}px;
                `}
              >
                <Radio name={name} checked={value} onChange={() => onChange(true)}>
                  <Text fontSize="small" fontWeight={600} color="neutral.600" mt="4">
                    Yes
                  </Text>
                </Radio>
                <Radio name={name} checked={!value} onChange={() => onChange(false)}>
                  <Text fontSize="small" fontWeight={600} color="neutral.600" mt="4">
                    No
                  </Text>
                </Radio>
              </Box>
            )}
          />
        </>
      )}

      {!isLumpSum && watchIsInflationAdjusted && (
        <Controller
          control={control}
          name={`Pensions.${index}.InflationRate`}
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Inflation Rate"
              decimalScale={2}
              allowNegative={false}
              rightIcon="far fa-percent"
              formVariant={errors.Pensions?.[index]?.InflationRate ? 'error' : undefined}
              helperText={errors.Pensions?.[index]?.InflationRate?.message}
              onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
              {...field}
            />
          )}
        />
      )}

      {isJointSurvivor && (
        <Controller
          control={control}
          name={`Pensions.${index}.SurvivorBenefit`}
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Survivor Benefit"
              decimalScale={2}
              allowNegative={false}
              rightIcon="far fa-percent"
              formVariant={errors.Pensions?.[index]?.SurvivorBenefit ? 'error' : undefined}
              helperText={errors.Pensions?.[index]?.SurvivorBenefit?.message}
              onValueChange={({ floatValue }) => onChange(floatValue ?? 0)}
              {...field}
            />
          )}
        />
      )}
    </Box>
  );
};
